/**
 * The PERIOD BASIS — the one class of error the dimension vector cannot catch.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY A SECOND AXIS EXISTS AT ALL
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `person-hours per WEEK` and `person-hours per YEAR` have the IDENTICAL
 * dimension vector: `TIME·PERSON`. Every check in `coverage-dimension.types.ts`
 * passes on both, and `coverage-ratio.ts` happily divides one by the other. The
 * result renders as a confident percentage that is wrong by the number of
 * working weeks in the year — 46× on the organisation the first slice models.
 *
 * That is not hypothetical. The design document's own worked numerator reads
 * `40 tickets/week × 12 min × 12 people × 0.8` with no `× 46 weeks`, over a
 * denominator of `12 people × 35 h/week × 46 weeks`. Written literally it is
 * hours per WEEK over person-hours per YEAR, and it is 46× too small.
 *
 * The dimension vector cannot see it because the accumulation WINDOW is not a
 * dimension. `12 minutes per ticket` and `40 tickets per week` both reduce to
 * plain magnitudes whose "per week"-ness lives in the input's NAME. A name is
 * not a type.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DURATION IS NOT A WINDOW — the distinction the whole file rests on
 * ══════════════════════════════════════════════════════════════════════════
 *
 * There are two different things measured in hours, and conflating them IS the
 * bug:
 *
 * 1. A DURATION — how long an activity takes. `12 minutes per ticket`,
 *    `35 hours worked`. This is the TIME dimension, canonicalised to hours by
 *    `coverage-time-unit.types.ts`, and it is unchanged by this file.
 * 2. An ACCUMULATION WINDOW — the span over which a quantity is counted.
 *    `40 tickets PER WEEK`, `19 320 person-hours PER YEAR`. This is the period
 *    basis, and it is what this file adds.
 *
 * A quantity's basis is the exponent of that window, exactly as a dimension is
 * the exponent of a base unit. It reuses the SAME type-level arithmetic —
 * `CoverageExponentSum` and `CoverageExponentDifference` — because it is the
 * same algebra over one more axis, not a new mechanism.
 *
 *     0   ABSOLUTE   a stock, a factor, a rate per work item:
 *                    `12 people`, `0.8`, `0.2 h/ticket`, `1 610 h`
 *    -1   ANNUAL     an amount accumulated over one year:
 *                    `1 840 tickets/year`, `19 320 person-hours/year`
 *
 * `mul` adds the exponents, `div` subtracts them, and `add`/`sub`/`min`/`max`
 * require them equal — the identical rules the dimension vector already obeys.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY EVERY PERIOD CANONICALISES TO THE YEAR, AND WHY THAT NEEDS NO `convert`
 * ══════════════════════════════════════════════════════════════════════════
 *
 * An author writes `perPeriod: "week"`. At INPUT BINDING the magnitude is
 * multiplied by the number of those periods in the organisation's working year
 * and the quantity becomes ANNUAL. `40 tickets per week` is 1 840 tickets per
 * year before it reaches the first step.
 *
 * This is deliberately the SAME mechanism `coverage-time-unit.types.ts` already
 * uses to turn `12 minutes` into `0.2 hours`, and it is the reason a seventh
 * function is not needed. The catalog in `coverage-formula.schema.ts` refused
 * `convert` on the grounds that "units are canonicalised to hours at input
 * binding, by exponent, so no in-model conversion exists to express". Periods
 * canonicalise to the year at input binding, by the same rule, so no in-model
 * annualisation exists to express either. The catalog stays at SIX.
 *
 * It also removes the step the design got wrong. A model that declares its
 * periods does not multiply by a `weeksPerYear` input at all, so there is no
 * `× 46` for an author to forget:
 *
 *     35 hours, perPeriod "week"   → 1 610 h/year, ANNUAL   ← no weeksPerYear
 *     40 tickets, perPeriod "week" → 1 840 tickets/year, ANNUAL
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHERE THIS AXIS DOES NOT REACH — read before relying on it
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `perPeriod` is OPT-IN, and it has to be: every model authored before it
 * existed declares no period, both sides come out ABSOLUTE, and two ABSOLUTE
 * sides agree with each other. So this axis catches a model that declares its
 * period on ONE side and forgets it on the other — which is the mistake the
 * design actually made — and it cannot catch a model that declares it nowhere.
 * Closing that last case means requiring a period at AUTHORING time, in the UI
 * slice, not here: making it mandatory in this module would refuse every model
 * that already exists.
 */
import type { CoverageExponent } from "./coverage-dimension.types";
import {
  type CoverageTimeUnit,
  type CoverageWorkingTimeSettings,
  hoursInCoverageTimeUnit
} from "./coverage-time-unit.types";

/**
 * The exponent of the annual accumulation window.
 *
 * Structurally a {@link CoverageExponent}: the same `[-6, +6]` range and the
 * same type-level arithmetic, so nothing about the exponent machinery is
 * duplicated for this axis.
 */
export type CoveragePeriodBasis = CoverageExponent;

/** A stock, a pure factor, or a rate per work item. Accumulates over nothing. */
export const COVERAGE_PERIOD_BASIS_ABSOLUTE = 0;

/** An amount accumulated over one working year. What an input's `perPeriod` produces. */
export const COVERAGE_PERIOD_BASIS_ANNUAL = -1;

/**
 * The bases a coverage ratio may be taken over.
 *
 * ABSOLUTE covers every model authored before this axis existed; ANNUAL covers
 * every model that declares its periods. Anything else — `year⁻²` from
 * annualising twice, `year¹` from dividing a total by a rate — is arithmetic
 * this engine can perform and is not a quantity of work, so it is refused where
 * the ratio is taken rather than mid-computation.
 *
 * That placement matches the dimension check exactly: a dimension is
 * unconstrained inside a formula and must be `TIME·PERSON` at the ratio. A
 * basis is unconstrained inside a formula for the same reason — an intermediate
 * with an odd exponent is legitimate algebra, and only the final quantity has
 * to be a quantity of work.
 */
export const COVERAGE_RATIO_ADMISSIBLE_PERIOD_BASES: readonly number[] = [
  COVERAGE_PERIOD_BASIS_ABSOLUTE,
  COVERAGE_PERIOD_BASIS_ANNUAL
];

/**
 * How many of `unit` fit in the organisation's WORKING year.
 *
 * Derived from the same working-time model the unit lengths come from, so
 * `week` yields the organisation's `workingWeeksPerYear` and not 52. A month is
 * one twelfth of the working year, so this returns exactly 12 for it — the
 * property `hoursInCoverageTimeUnit` already guarantees.
 *
 * ONE division, in a fixed order, on values `hoursInCoverageTimeUnit` produced.
 * `Math.pow` is not reachable from here, so the determinism guarantee in
 * `coverage-evaluator.ts` extends over this axis unchanged.
 */
export function coveragePeriodsPerYear(
  unit: CoverageTimeUnit,
  settings: CoverageWorkingTimeSettings
): number {
  return hoursInCoverageTimeUnit("year", settings) / hoursInCoverageTimeUnit(unit, settings);
}

/** True when a basis is an integer inside the representable exponent range. */
export function isRepresentableCoveragePeriodBasis(basis: number): boolean {
  return Number.isInteger(basis) && basis >= -6 && basis <= 6;
}

/** True when a basis is one a coverage ratio may be taken over. */
export function isAdmissibleCoverageRatioPeriodBasis(basis: number): boolean {
  return COVERAGE_RATIO_ADMISSIBLE_PERIOD_BASES.includes(basis);
}

/**
 * A stable, locale-free rendering of a basis, for warning messages.
 *
 * Deterministic by construction, like `formatCoverageDimension`: fixed strings,
 * no `Intl`, no `toLocaleString`.
 */
export function formatCoveragePeriodBasis(basis: number): string {
  if (basis === COVERAGE_PERIOD_BASIS_ABSOLUTE) {
    return "absolute";
  }
  if (basis === COVERAGE_PERIOD_BASIS_ANNUAL) {
    return "per year";
  }
  return basis < 0 ? `per year^${-basis}` : `year^${basis}`;
}
